import React, { useCallback, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import {
  DatePicker,
  Form,
  Input,
  Tag,
  Spin,
  Typography,
  Tooltip,
  Space,
  Button,
  Modal,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  QuestionCircleTwoTone,
} from '@ant-design/icons';
import cn from 'classnames';
// Constants
import { commonTimeFormat } from 'constants/global';
// Api
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import { GET_VIDEO_EDIT_REQUESTS_FOR_EDITORS } from 'api/videoLab/queries';
import {
  DELETE_EDITED_VIDEO_BY_ID,
  EDIT_VIDEO_REQUEST_INFO,
} from 'api/videoLab/mutation';
import {
  GetVideoEditRequests_getVideoEditRequests_entities,
  GetVideoEditRequests_getVideoEditRequests_entities_editedVideos,
} from 'api/videoLab/types/GetVideoEditRequests';
import {
  EditVideoRequestInfo,
  EditVideoRequestInfoVariables,
} from 'api/videoLab/types/EditVideoRequestInfo';
import {
  VideoEditRequestStatus,
  VideoEditRequestSubmissionStatus,
} from 'api/graphql-global-types';
// Types
import {
  CreateMentionInput,
  CreateUnregisteredContactInput,
} from 'api/graphql-global-types';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import {
  DeleteEditedVideoById,
  DeleteEditedVideoByIdVariables,
} from 'api/videoLab/types/DeleteEditedVideoById';
// UI
import { Mentions, MentionValues } from 'uiShared/Mentions/Mentions';
import TagsInput from 'uiShared/TagsInput/TagsInput';
import { tagStyles } from 'uiShared/TagWithButtons/helpers';
import { errorNotification, successNotification } from 'ui/Notification';
// Helpers
import { formatHashtagInput } from 'helpers/hashtags';
import { formatMentionsInput } from 'helpers/mentions';
// Components
import { UploadFinalizedVideo } from '../UploadFinalizedVideo/UploadFinalizedVideo';
import VideoRequestSourceFields from '../../common/VideoRequestSourceFields/VideoRequestSourceFields';
// Styles
import styles from './MyVideoEditRequestDetailsModal.module.scss';

const { Text } = Typography;

export type FormInputs = {
  selectedVideoType: string;
  videoEditorId: string | null;
  createdAt: Moment;
  dueDate: Moment | null;
  rawUrls: string | null;
  adminNotes: string | null;
  notes: string | null;
};

type MyVideoEditRequestDetailsModalProps = {
  videoEditRequest: GetVideoEditRequests_getVideoEditRequests_entities | null;
  hideDetailsModal: () => void;
  isViewOnly?: boolean;
  getVideoRequestsInput: () => any;
};

const MyVideoEditRequestDetailsModal = ({
  videoEditRequest,
  hideDetailsModal,
  isViewOnly,
  getVideoRequestsInput,
}: MyVideoEditRequestDetailsModalProps): JSX.Element | null => {
  const initialFormValues: FormInputs = {
    selectedVideoType: 'Interview',
    videoEditorId: videoEditRequest?.assignedEditorId || null,
    createdAt: moment(videoEditRequest?.createdAt) || '-',
    dueDate: moment(videoEditRequest?.dueDate) || '-',
    rawUrls: videoEditRequest?.rawUrls?.join(', ') || null,
    adminNotes: videoEditRequest?.adminNotes || null,
    notes: videoEditRequest?.notes || null,
  };
  const [editedVideos, setEditedVideos] = useState<
    GetVideoEditRequests_getVideoEditRequests_entities_editedVideos[]
  >(videoEditRequest?.editedVideos || []);

  const [selectedEditedVideo, setSelectedEditedVideo] =
    useState<GetVideoEditRequests_getVideoEditRequests_entities_editedVideos | null>(
      null
    );
  const [hashtagValues, setHashtagValues] = useState(
    videoEditRequest?.hashtags?.map((tag) => tag.name) || []
  );

  const [mentionsValues, setMentionsValues] = useState<MentionValues[]>(
    videoEditRequest?.mentions || []
  );
  const [isUploadModalVisible, setIsUploadModalVisible] =
    useState<boolean>(false);

  const showUploadModal = () => setIsUploadModalVisible(true);

  const hideUploadModal = () => {
    setIsUploadModalVisible(false);
    setSelectedEditedVideo(null);
  };

  const [editVideoRequestInfo, { loading: editVideoRequestInfoLoading }] =
    useMutation<EditVideoRequestInfo, EditVideoRequestInfoVariables>(
      EDIT_VIDEO_REQUEST_INFO
    );

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const [deleteEditedVideoById] = useMutation<
    DeleteEditedVideoById,
    DeleteEditedVideoByIdVariables
  >(DELETE_EDITED_VIDEO_BY_ID);

  const { refetch: refetchVideoEditData } = useQuery(
    GET_VIDEO_EDIT_REQUESTS_FOR_EDITORS,
    {
      variables: {
        ...getVideoRequestsInput(),
      },
    }
  );

  const [form] = Form.useForm();

  const handleFormFinish = async () => {
    const values = form.getFieldsValue();

    const formattedHashtagValues = hashtagValues?.length
      ? formatHashtagInput(hashtagValues)
      : null;

    const createUnregisteredContactInput =
      (mentionsValues as MentionValues[])?.filter(
        (mention) => !mention?.targetType
      ) || [];

    const unregisterContactsData = await createUnregisteredContact({
      variables: {
        input:
          createUnregisteredContactInput as CreateUnregisteredContactInput[],
      },
    });

    const newUnregisteredContacts =
      (unregisterContactsData?.data?.createUnregisteredContact || [])?.map(
        (contact) => ({
          unregisteredContactId: contact?.id,
        })
      ) || [];

    const oldMentions: MentionValues[] =
      mentionsValues?.filter((mention) => mention?.targetType) || [];
    const formatedOldMentions = formatMentionsInput(oldMentions);

    try {
      await editVideoRequestInfo({
        variables: {
          input: {
            id: videoEditRequest?.id || '',
            notes: values.notes,
            status: VideoEditRequestStatus.OnReview,
            hashtagInputs: formattedHashtagValues,
            mentionsInputs: [
              ...formatedOldMentions,
              ...newUnregisteredContacts,
            ] as CreateMentionInput[],
          },
        },
      });

      successNotification('Information updated successfully');
      hideDetailsModal();
      refetchVideoEditData();
    } catch (err) {
      errorNotification((err as Error)?.message || 'Something went wrong');
    }
  };

  const handleMentionChange = useCallback(
    (mentions) => {
      if (JSON.stringify(mentions) !== JSON.stringify(mentionsValues)) {
        return setMentionsValues(mentions);
      }
    },
    [mentionsValues]
  );

  useEffect(() => {
    if (videoEditRequest) {
      form.setFieldsValue({
        editorNotes: videoEditRequest?.notes || '',
      });
    }
  }, [videoEditRequest, form]);

  if (editVideoRequestInfoLoading) {
    return <Spin />;
  }

  const handleEditedVideoClick = (
    editedVideo: GetVideoEditRequests_getVideoEditRequests_entities_editedVideos
  ) => {
    setSelectedEditedVideo(editedVideo);
    showUploadModal();
  };

  const handleRemoveEditedVideo = async (
    editedVideoId: string,
    key: string
  ) => {
    try {
      await deleteEditedVideoById({
        variables: { input: { editedVideoId, key } },
      });
      successNotification('The stream has been deleted successfully');
      handleRefetchVideos();
    } catch (err: any) {
      errorNotification(err?.message);
    }
  };

  const handleRefetchVideos = async () => {
    const { data: refetchedData } = await refetchVideoEditData();
    const videoRequestItemEditedVideos =
      refetchedData?.getVideoEditRequests?.entities?.filter(
        (item: GetVideoEditRequests_getVideoEditRequests_entities) =>
          item?.id === videoEditRequest?.id
      )[0]?.editedVideos;
    setEditedVideos(videoRequestItemEditedVideos);
  };

  const handleCopyLinkButtonClick = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);

      successNotification('Link copied');
    } catch (error) {
      errorNotification('Could not copy text');
    }
  };

  // video is rejected if any submission is rejected with the latest reason not being empty
  const checkIsRejected = (
    editedVideo: GetVideoEditRequests_getVideoEditRequests_entities_editedVideos
  ): boolean => {
    const isItemRejected: boolean = editedVideo.reviewSubmissions.some(
      (submission) =>
        submission.status === VideoEditRequestSubmissionStatus.Rejected &&
        submission.rejections
          ? !!submission.rejections[submission.rejections.length - 1].reason
          : false
    );

    return isItemRejected;
  };

  // flag used to determine user options-if the request was rejected,
  // we have different UI and options
  const isReupload: boolean =
    videoEditRequest?.status === VideoEditRequestStatus.Rejected;

  return (
    <>
      <Form
        form={form}
        layout="horizontal"
        name="videoRequestDetailsForm"
        autoComplete="off"
        initialValues={initialFormValues}
        onFinish={handleFormFinish}
      >
        <div>
          <p>Task ID: {videoEditRequest?.id || ''}</p>
        </div>

        <div>
          <p>Video edit request status: {videoEditRequest?.status || ''}</p>
        </div>

        <div>
          <p>Store name: {videoEditRequest?.assignedEditor?.firstName || ''}</p>
        </div>

        {!!videoEditRequest?.additionalStores.length && (
          <Form.Item label={<Text>Additional store names</Text>}>
            <div>
              {videoEditRequest?.additionalStores.map((store, index) => (
                <Tag key={index} style={tagStyles}>
                  <div className={styles.text}>
                    {store?.storeDetails?.storeName || ''}
                  </div>
                </Tag>
              ))}
            </div>
          </Form.Item>
        )}

        <VideoRequestSourceFields source={videoEditRequest?.videoSource} />

        <Form.Item name="createdAt" label={<Text>Created at</Text>}>
          <DatePicker
            data-testid="createdAt"
            showTime
            format={commonTimeFormat}
            style={{ width: 250 }}
            disabled
          />
        </Form.Item>

        <Form.Item name="dueDate" label={<Text>Due date</Text>}>
          <DatePicker
            data-testid="dueDate"
            showTime
            format={commonTimeFormat}
            style={{ width: 250 }}
            minuteStep={15}
            disabled
          />
        </Form.Item>

        <Form.Item label={<Text>Raw video link</Text>}>
          <div style={{ marginTop: '10px' }}>
            {videoEditRequest?.rawUrls.map((url, index) => (
              <Tag key={index} style={tagStyles}>
                <div className={styles.text}>{url}</div>

                <Button
                  type="link"
                  icon={<ShareAltOutlined />}
                  onClick={() => handleCopyLinkButtonClick(url)}
                  className={styles.tagButton}
                />
              </Tag>
            ))}
          </div>
        </Form.Item>

        <Form.Item label={<Text>Finalized Video</Text>}>
          <div className={styles.editedVideosList}>
            {editedVideos?.map((item, index) => {
              const isItemRejected: boolean = checkIsRejected(item);

              // allow delete unless the file is submitted for review-after that point, don't allow any deletions
              // so we are checking if it's not "view only" and if it's also missing review submissions
              const allowDelete =
                !isViewOnly && !item.reviewSubmissions?.length;

              return (
                <Tag
                  key={index}
                  onClick={() => handleEditedVideoClick(item)}
                  className={cn(styles.videoTag, {
                    [styles.videoTagError]: isItemRejected,
                  })}
                >
                  {item.title || `finalized_video_${index}`}
                  {allowDelete && (
                    <Button
                      className={styles.videoTagButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveEditedVideo(item.id, item.key);
                      }}
                    >
                      <DeleteOutlined />
                    </Button>
                  )}
                </Tag>
              );
            })}
          </div>
        </Form.Item>

        {!isViewOnly && !isReupload && (
          <Space className="video-modal-container" direction="vertical">
            <Button
              type="primary"
              onClick={showUploadModal}
              className={styles.addButton}
            >
              {<UploadOutlined />}
              Upload finalized video
            </Button>
          </Space>
        )}

        <Form.Item name="adminNotes" label={<Text>Admin Notes</Text>}>
          <Input disabled />
        </Form.Item>

        <Form.Item name="notes" label={<Text>Editor Notes</Text>}>
          <Input placeholder="Add notes here" disabled={isViewOnly} />
        </Form.Item>

        <Form.Item name="hashtags" label="Tagging">
          <TagsInput
            hashtags={hashtagValues}
            onChange={(value: any) => setHashtagValues(value)}
            name="hashtags"
          />
        </Form.Item>

        <Form.Item name="mentions" className={styles.mentionsRoot}>
          <Text className={styles.mentionSectionTitleWrapper}>
            <p className={styles.mentionsSectionTitle}>Mentioning options</p>
            <Tooltip
              title="Mention athletes, brands, and organizations by using their names and
          links to directly engage them and increase interaction on your
          content. Write down their names and url links."
            >
              <QuestionCircleTwoTone />
            </Tooltip>
          </Text>
          <div className={styles.mentionsContainer}>
            <Mentions
              mentions={mentionsValues}
              onChange={handleMentionChange}
            />
          </div>
        </Form.Item>

        {!isViewOnly && (
          <Form.Item className={styles.saveButtonContainer}>
            <Button
              type="primary"
              htmlType="submit"
              loading={editVideoRequestInfoLoading}
              disabled={!editedVideos?.length}
            >
              Submit for review
            </Button>
          </Form.Item>
        )}
      </Form>

      <Modal
        title={
          selectedEditedVideo
            ? 'Edit finalized video'
            : 'Upload finalized video'
        }
        visible={!!isUploadModalVisible}
        footer={null}
        onCancel={hideUploadModal}
        width={800}
        destroyOnClose
      >
        <UploadFinalizedVideo
          onClose={hideUploadModal}
          isRejected={
            (selectedEditedVideo && !!checkIsRejected(selectedEditedVideo)) ??
            false
          }
          isAdmin={false}
          videoEditRequest={videoEditRequest}
          selectedEditedVideo={selectedEditedVideo}
          handleRefetchVideos={handleRefetchVideos}
        />
      </Modal>
    </>
  );
};

export default MyVideoEditRequestDetailsModal;
